import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Category tabs (DESIGN.md §7): 16 px semibold, fill.neutral at rest,
 * fill.selected with stroke.brand active, 48 px tall, horizontal scroll with
 * no fade. Every pack's category strip is this component — menu sections,
 * departments, treatment groups, class types.
 */
export interface CategoryTab {
  id: string;
  label: string;
  count?: number;
}

export function CategoryTabs({
  tabs,
  activeId,
  onSelect,
  ariaLabel = "Categories",
  className,
}: Readonly<{
  tabs: readonly CategoryTab[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel?: string;
  className?: string;
}>) {
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn("flex gap-2 overflow-x-auto", className)}>
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(tab.id)}
            className={cn(
              "min-h-target shrink-0 rounded-control border px-4 font-sans text-base font-semibold",
              "transition-colors duration-150 ease-counter",
              active
                ? "border-stroke-brand bg-fill-selected text-text-strong"
                : "border-transparent bg-fill-neutral text-text-weak",
            )}
          >
            {tab.label}
            {typeof tab.count === "number" ? (
              <span className="ml-2 font-ledger tabular-nums text-text-weak">{tab.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
